import { reflect } from '@effector/reflect';
import { NumberInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm } from 'shared/ui';
import { EditBonusModel } from '../../model';

export const createEditBonusModal = (model: EditBonusModel) => {
	const ModalForm = createModalForm(model);

	const DepositRateField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: '%',
			'data-test-id': 'edit-bonus-deposit-rate-input',
			label: translated('edit-bonus.deposit-rate'),
			placeholder: translated('edit-bonus.deposit-rate-placeholder'),
			value: model.form.fields.depositRate.$value,
			onChange: (value) => model.form.fields.depositRate.change(String(value ?? '')),
			error: combine(
				[model.form.fields.depositRate.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-bonus.deposit-rate'),
					}),
			),
		},
	});

	const ActivationThresholdField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: model.$modalData.map((bonus) => bonus?.currency ?? null),
			'data-test-id': 'edit-bonus-activation-threshold-input',
			label: translated('edit-bonus.activation-threshold'),
			placeholder: translated('edit-bonus.activation-threshold-placeholder'),
			value: model.form.fields.activationThreshold.$value,
			onChange: (value) => model.form.fields.activationThreshold.change(String(value ?? '')),
			error: combine(
				[model.form.fields.activationThreshold.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-bonus.activation-threshold'),
					}),
			),
		},
	});

	const EditBonusModalActionIcon = reflect({
		view: ModalForm.ActionIcon,
		bind: {
			'data-test-id': 'edit-bonus-action-icon',
			disabled: viewerModel.$role.map((role) => role !== USER_ROLES.god),
			variant: 'ghost-secondary',
			children: <PenSquare />,
		},
	});

	const EditBonusModal = () => {
		const { t } = useTranslation();

		const [pending, pristine] = useUnit([model.$pending, model.form.$pristine]);

		return (
			<ModalForm
				title={t('edit-bonus.title')}
				cancelButtonProps={{
					children: t('edit-bonus.cancel'),
				}}
				submitButtonProps={{
					children: t('edit-bonus.save'),
					loading: pending,
					disabled: pristine,
				}}
			>
				<DepositRateField />
				<ActivationThresholdField />
			</ModalForm>
		);
	};

	return Object.assign(EditBonusModal, {
		ActionIcon: EditBonusModalActionIcon,
	});
};
